import { PresetTableCell } from './PresetTableCell';
import type { SetTableViewProps } from './useSeededKidSetTable';
import {
  presetTableBase,
  presetTableCellBase,
  presetTableHeaderCellBase,
} from './useTableStyles';

type ScenarioRow = {
  formRowIndex: number;
  scenario: string;
  scenarioRowSpan?: number;
  description: string;
  values: string[];
};

function buildScenarioRows(dataRows: string[][]): ScenarioRow[] {
  const rows: ScenarioRow[] = [];

  dataRows.forEach((row, rowIndex) => {
    const formRowIndex = rowIndex + 1;
    const scenario = row[0] ?? '';
    const description = row[1] ?? '';
    const values = row.slice(2);

    if (scenario) {
      const nextRow = dataRows[rowIndex + 1];
      const rowSpan = nextRow && !nextRow[0] ? 2 : 1;
      rows.push({
        formRowIndex,
        scenario,
        scenarioRowSpan: rowSpan,
        description,
        values,
      });
    } else {
      rows.push({ formRowIndex, scenario: '', description, values });
    }
  });

  return rows;
}

/**
 * Performance Scenarios preset table (presentational).
 * Marker-injected cells (FIG scenario amounts/returns, FUND holding period) are read-only.
 */
export function PerformanceScenariosTable({
  content,
  updateCell,
  isInjectedCell,
}: SetTableViewProps) {
  const [headerRow, ...dataRows] = content;
  const colCount = headerRow.length;
  const valueColCount = Math.max(colCount - 2, 0);
  const valueColWidth =
    valueColCount > 0 ? `${Math.floor(50 / valueColCount)}%` : undefined;
  const rows = buildScenarioRows(dataRows);

  return (
    <table className={presetTableBase}>
      <colgroup>
        <col style={{ width: '14%' }} />
        <col style={{ width: '36%' }} />
        {Array.from({ length: valueColCount }).map((_, index) => (
          <col key={index} style={{ width: valueColWidth }} />
        ))}
      </colgroup>
      <thead>
        <tr>
          {headerRow.map((cell, cellIndex) => (
            <th
              key={cellIndex}
              className={`${presetTableHeaderCellBase} text-left`}
            >
              <PresetTableCell
                value={cell}
                rowIndex={0}
                colIndex={cellIndex}
                isInjected={isInjectedCell(0, cellIndex)}
                onUpdate={updateCell}
                multiline={cellIndex <= 1}
              />
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const isScenarioStart = Boolean(row.scenario);

          return (
            <tr key={row.formRowIndex}>
              {isScenarioStart ? (
                <td
                  rowSpan={row.scenarioRowSpan}
                  className={`${presetTableCellBase} font-semibold`}
                >
                  <PresetTableCell
                    value={row.scenario}
                    rowIndex={row.formRowIndex}
                    colIndex={0}
                    isInjected={isInjectedCell(row.formRowIndex, 0)}
                    onUpdate={updateCell}
                    multiline
                  />
                </td>
              ) : null}
              <td className={presetTableCellBase}>
                <PresetTableCell
                  value={row.description}
                  rowIndex={row.formRowIndex}
                  colIndex={1}
                  isInjected={isInjectedCell(row.formRowIndex, 1)}
                  onUpdate={updateCell}
                  multiline
                />
              </td>
              {Array.from({ length: valueColCount }).map((_, valueIndex) => {
                const colIndex = valueIndex + 2;

                return (
                  <td key={colIndex} className={presetTableCellBase}>
                    <PresetTableCell
                      value={row.values[valueIndex] ?? ''}
                      rowIndex={row.formRowIndex}
                      colIndex={colIndex}
                      isInjected={isInjectedCell(row.formRowIndex, colIndex)}
                      onUpdate={updateCell}
                    />
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
